import React, { useState } from "react";
import { Link, Outlet, useLocation, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { LayoutDashboard, FileText, MessageSquare, Star, Mail, LogOut, Settings, MapPin, Home } from "lucide-react";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
  { to: "/admin/blog", icon: FileText, label: "Blog Posts" },
  { to: "/admin/reviews", icon: Star, label: "Reviews" },
  { to: "/admin/comments", icon: MessageSquare, label: "Comments" },
  { to: "/admin/contact", icon: Mail, label: "Contact Submissions" },
  { to: "/admin/cities", icon: MapPin, label: "City Pages" },
  { to: "/admin/site", icon: Settings, label: "Site Content" },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
      <aside className="w-64 bg-brand-navy text-white flex flex-col hidden lg:flex">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-white p-1.5 flex items-center justify-center">
              <img
                src="https://img1.wsimg.com/isteam/ip/6251f5ed-3ec2-463f-9bc9-248d796842b9/Untitled%20design%20(2)-01%20(1).png/:/rs=w:200,h:200,cg:true,m"
                alt="Pool Guru logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <div className="font-heading font-extrabold">Pool Guru</div>
              <div className="text-xs text-slate-400">Admin CMS</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((item) => {
            const active = item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                  active ? "bg-brand-pink text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <item.icon className="w-4 h-4" /> {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="admin-view-site"
          >
            <Home className="w-4 h-4" /> View site
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold text-slate-300 hover:bg-red-500/20 hover:text-red-400 transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between lg:hidden">
          <button onClick={() => setMobileOpen(!mobileOpen)} className="font-semibold">Menu</button>
          <button onClick={handleLogout} className="text-sm text-slate-600">Log out</button>
        </header>
        {mobileOpen && (
          <div className="lg:hidden bg-white border-b border-slate-200 p-4 space-y-1">
            {NAV.map((item) => (
              <Link key={item.to} to={item.to} onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-semibold">
                {item.label}
              </Link>
            ))}
          </div>
        )}
        <main className="flex-1 p-6 lg:p-10 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
